import React, { useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    UserCircleIcon,
    ChatBubbleLeftRightIcon,
    QueueListIcon,
    BookOpenIcon,
    ChevronDownIcon,
    BanknotesIcon,
    UsersIcon,
    KeyIcon,
    SignalIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { activeNavHref, SidebarFlyout, Tooltip, useAnchoredFlyout } from '@odo/shared-ui';
import homeIcon from '../../assets/icons/home.svg';
import logoutIcon from '../../assets/icons/log-out.svg';
import logo from '../../assets/icons/odo-logo-res.png';
import { WhatsNewNavItem } from '../whats-new/WhatsNewNavItem';
import { useSidebarCounts, type NavBadgeCounts } from '../../hooks/useSidebarCounts';
import { useUser } from '../../providers/UserProvider';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import type { KnownPermission } from '../../services/iam';
import {
    messagePatientsBadgeLabel,
    monitoringBadgeCount,
    monitoringTasksBadgeLabel,
    notificationPatientsBadgeLabel
} from '../monitoring/shared/monitoringCounts';
import { plural } from '../../utils/plural';

/** Pill when expanded, dot when collapsed. Shown only above zero. */
interface NavBadge {
    count: number;
    label: (count: number) => string;
}

interface SubNavItem {
    label: string;
    href: string;
    /** Not built yet - rendered but inert, with the "SOON" pill. */
    soon?: boolean;
    badge?: NavBadge;
}

interface NavItem {
    icon: React.ReactNode;
    label: string;
    href?: string;
    subItems?: SubNavItem[];
    badge?: NavBadge;
}

function NavCountBadge({ badge }: { badge: NavBadge | undefined }) {
    const count = badge?.count ?? 0;
    if (!badge || count <= 0) return null;
    return (
        <span className="bg-yellow-400 text-gray-800 px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-auto">
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">{badge.label(count)}</span>
        </span>
    );
}

// aria-label on a bare span is skipped by screen readers, so the label rides in sr-only text.
function NavBadgeDot({ label }: { label: string }) {
    return (
        <>
            <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-yellow-400 rounded-full border border-white"
            />
            <span className="sr-only">{label}</span>
        </>
    );
}

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    /** Takes the rail out of the layout (focus mode) without losing its state. */
    hidden?: boolean;
}

// Single source of truth for the two widths below - also mirrored into the
// `--odo-table-overlay-left` CSS var so a DataTable's expanded overlay never
// covers this sidebar. Kept as plain numbers because Tailwind's `w-[60px]`/
// `w-[220px]` arbitrary values below must stay literal strings for the JIT
// scanner to pick up; these constants exist to keep the CSS var in sync with them.
const SIDEBAR_WIDTH_COLLAPSED_PX = 60;
const SIDEBAR_WIDTH_EXPANDED_PX = 220;
const OVERLAY_LEFT_CSS_VAR = '--odo-table-overlay-left';

function monitoringNavItem(counts: NavBadgeCounts): NavItem {
    return {
        icon: <SignalIcon className="h-4 w-4" />,
        label: 'Monitoring',
        badge: {
            count: monitoringBadgeCount({
                notificationPatients: counts.dmNotificationPatients,
                messagePatients: counts.dmMessagePatients
            }),
            label: monitoringTasksBadgeLabel
        },
        subItems: [
            { label: 'Overview', href: '/monitoring/overview' },
            {
                label: 'Notifications',
                href: '/monitoring/notifications',
                badge: {
                    count: counts.dmNotificationPatients,
                    label: notificationPatientsBadgeLabel
                }
            },
            {
                label: 'Messages',
                href: '/monitoring/messages',
                badge: {
                    count: counts.dmMessagePatients,
                    label: messagePatientsBadgeLabel
                }
            }
        ]
    };
}

// Module-level so the component stays focused on behavior; gated sections
// appear once permissions load (hasPermission is false until then).
function buildNavItems(
    hasPermission: (permission: KnownPermission) => boolean,
    counts: NavBadgeCounts
): NavItem[] {
    const pendingChargesBadge: NavBadge = {
        count: counts.pendingCharges,
        label: (count) => plural(count, 'pending charge')
    };
    return [
        {
            icon: <img src={homeIcon} alt="Home" className="h-4 w-4" />,
            label: 'Home',
            href: '/home',
            badge: {
                count: counts.myOpenCases,
                label: (count) => `${count} open cases assigned to you`
            }
        },
        {
            icon: <UserGroupIcon className="h-4 w-4" />,
            label: 'Patients',
            href: '/patients'
        },
        {
            icon: <QueueListIcon className="h-4 w-4" />,
            label: 'Work Queue',
            href: '/work-queue'
        },
        {
            icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
            label: 'Conversations',
            href: '/conversations',
            badge: {
                count: counts.unreadConversations,
                label: (count) => `${count} unread conversations`
            }
        },
        monitoringNavItem(counts),
        ...(hasPermission('billing.get')
            ? [
                  {
                      icon: <BanknotesIcon className="h-4 w-4" />,
                      label: 'Billing',
                      badge: pendingChargesBadge,
                      subItems: [
                          { label: 'Overview', href: '/billing/overview' },
                          {
                              label: 'Charges',
                              href: '/billing/charges',
                              badge: pendingChargesBadge
                          },
                          { label: 'Invoices', href: '/billing/invoices' },
                          { label: 'One-time discounts', href: '/billing/one-time-discounts' },
                          {
                              label: 'Purchase orders',
                              href: '/billing/purchase-orders'
                          },
                          { label: 'Products', href: '/billing/products' },
                          { label: 'Vendors', href: '/billing/vendors' },
                          { label: 'Discounts', href: '/billing/discounts' }
                      ]
                  }
              ]
            : []),
        ...(hasPermission('practices.get')
            ? [
                  {
                      icon: <UsersIcon className="h-4 w-4" />,
                      label: 'Accounts',
                      subItems: [
                          { label: 'Overview', href: '/accounts/overview' },
                          { label: 'Practices', href: '/accounts/practices' },
                          { label: 'Orthodontists', href: '/accounts/orthodontists' },
                          { label: 'Communications', href: '/accounts/communications' },
                          { label: 'Users', href: '/accounts/users' },
                          { label: 'Events', href: '/accounts/events' }
                      ]
                  }
              ]
            : []),
        ...(hasPermission('iam.get')
            ? [
                  {
                      icon: <KeyIcon className="h-4 w-4" />,
                      label: 'Access',
                      subItems: [
                          { label: 'Users', href: '/access/users' },
                          { label: 'Teams', href: '/access/teams' },
                          { label: 'Roles', href: '/access/roles' },
                          { label: 'Permissions', href: '/access/permissions' },
                          { label: 'Routes', href: '/access/routes' }
                      ]
                  }
              ]
            : []),
        {
            icon: <BookOpenIcon className="h-4 w-4" />,
            label: 'Resources',
            subItems: [
                { label: 'Overview', href: '/resources' },
                { label: 'Help Center', href: '/resources/help-center' },
                { label: 'Videos', href: '/resources/videos' },
                { label: 'FAQ', href: '/resources/faq' }
            ]
        },
        {
            icon: <UserCircleIcon className="h-4 w-4" />,
            label: 'Account',
            href: '/account'
        }
    ];
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, hidden = false }) => {
    const navigate = useNavigate();
    const location = useLocation();

    useLayoutEffect(() => {
        const visibleWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED_PX : SIDEBAR_WIDTH_EXPANDED_PX;
        const width = hidden ? 0 : visibleWidth;
        document.documentElement.style.setProperty(OVERLAY_LEFT_CSS_VAR, `${width}px`);
    }, [collapsed, hidden]);
    const { logout } = useUser();
    // IAM-gated sections (stored grants via GET /iam/me). While loading,
    // hasPermission is false, so gated items simply appear once known.
    const { hasPermission } = useMyPermissions();
    const badgeCounts = useSidebarCounts();
    const [openMenu, setOpenMenu] = useState<string | null>(
        location.pathname.startsWith('/resources')
            ? 'Resources'
            : location.pathname.startsWith('/billing')
              ? 'Billing'
              : location.pathname.startsWith('/accounts')
                ? 'Accounts'
                : location.pathname.startsWith('/access')
                  ? 'Access'
                  : location.pathname.startsWith('/monitoring')
                    ? 'Monitoring'
                    : null
    );

    // Collapsed-mode flyout submenu; the shared hook owns open/dismiss
    // (Escape, outside click, resize, re-expand). asideRef marks the host.
    const {
        flyout,
        toggle: toggleFlyout,
        close: closeFlyout,
        containerRef: asideRef
    } = useAnchoredFlyout<HTMLElement>({ enabled: collapsed && !hidden, closeOnResize: true });

    const navItems = buildNavItems(hasPermission, badgeCounts);

    const handleNavClick = (item: NavItem, event: React.MouseEvent<HTMLButtonElement>) => {
        if (item.subItems) {
            if (collapsed) {
                const rect = event.currentTarget.getBoundingClientRect();
                toggleFlyout(item.label, { left: rect.right, top: rect.top });
            } else {
                setOpenMenu((prev) => (prev === item.label ? null : item.label));
            }
        } else if (item.href) {
            closeFlyout();
            navigate(item.href);
        }
    };

    const flyoutItem = flyout ? navItems.find((i) => i.label === flyout.key) : null;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (hidden) return null;

    return (
        <aside
            ref={asideRef}
            className={`flex flex-col h-full bg-white border-r border-gray-200 shadow-sm transition-all duration-300 ${collapsed ? 'w-[60px]' : 'w-[220px]'}`}
        >
            <div className="flex flex-col items-start gap-10 px-2 pt-4 w-full flex-1">
                {/* Logo + toggle */}
                <div
                    className={`flex items-center w-full ${collapsed ? 'flex-col gap-3' : 'justify-between px-2'}`}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        className={`object-contain cursor-pointer ${collapsed ? 'w-8 h-8' : 'w-[95px] h-10'}`}
                        onClick={() => navigate('/home')}
                    />
                    <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                        <button
                            onClick={onToggle}
                            className="p-1 rounded hover:bg-gray-100 transition-colors"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="#9ca3af"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
                            >
                                <rect x="2" y="2" width="16" height="16" rx="3" />
                                <line x1="8" y1="2" x2="8" y2="18" />
                                <polyline points="14,8 11,10 14,12" />
                            </svg>
                        </button>
                    </Tooltip>
                </div>

                {/* Nav items */}
                <nav className="flex flex-col items-start gap-1.5 w-full">
                    {navItems.map((item) => {
                        // Collapsed parents light up from an active sub-route or open flyout.
                        const isFlyoutOpen = collapsed && flyout?.key === item.label;
                        const isActive = item.href
                            ? location.pathname === item.href ||
                              location.pathname.startsWith(`${item.href}/`)
                            : collapsed && item.subItems
                              ? isFlyoutOpen ||
                                item.subItems.some(
                                    (s) =>
                                        location.pathname === s.href ||
                                        location.pathname.startsWith(`${s.href}/`)
                                )
                              : false;
                        const isMenuOpen = !collapsed && openMenu === item.label;
                        const badgeCount = item.badge?.count ?? 0;
                        const showBadge = badgeCount > 0;
                        const badgeLabel = item.badge?.label(badgeCount);
                        // Longest match wins, so "Overview" (/resources) does not
                        // stay lit while a child section is open.
                        const activeSubHref = item.subItems
                            ? activeNavHref(
                                  location.pathname,
                                  item.subItems.map((sub) => sub.href)
                              )
                            : undefined;

                        return (
                            <React.Fragment key={item.label}>
                                <div className="w-full relative">
                                    <Tooltip
                                        content={item.label}
                                        side="right"
                                        disabled={!collapsed || isFlyoutOpen}
                                    >
                                        <button
                                            onClick={(e) => handleNavClick(item, e)}
                                            aria-haspopup={item.subItems ? 'true' : undefined}
                                            aria-expanded={
                                                item.subItems
                                                    ? isFlyoutOpen || isMenuOpen
                                                    : undefined
                                            }
                                            className={`flex h-10 items-center gap-2 px-3 py-2 w-full rounded-lg transition-colors duration-200 hover:bg-gray-100 overflow-hidden ${collapsed ? 'justify-center' : ''} ${isActive ? 'bg-gray-200' : ''}`}
                                        >
                                            <div className="flex w-5 h-5 items-center justify-center text-gray-600 flex-shrink-0 relative">
                                                {item.icon}
                                                {/* Collapsed badge: dot on icon */}
                                                {collapsed && showBadge && badgeLabel && (
                                                    <NavBadgeDot label={badgeLabel} />
                                                )}
                                            </div>
                                            {!collapsed && (
                                                <>
                                                    <span
                                                        className={`text-sm truncate min-w-0 ${
                                                            isActive
                                                                ? 'font-semibold text-gray-900'
                                                                : 'font-medium text-gray-700'
                                                        }`}
                                                    >
                                                        {item.label}
                                                    </span>
                                                    <NavCountBadge badge={item.badge} />
                                                    {item.subItems && (
                                                        <ChevronDownIcon
                                                            className={`h-4 w-4 text-gray-400 flex-shrink-0 ${showBadge ? 'ml-1' : 'ml-auto'} transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    </Tooltip>
                                </div>

                                {/* Submenu (expanded mode only) */}
                                {item.subItems && (
                                    <div
                                        className="transition-all duration-300 ease-in-out overflow-hidden w-full"
                                        style={{
                                            maxHeight: isMenuOpen
                                                ? `${item.subItems.length * 40}px`
                                                : '0px'
                                        }}
                                    >
                                        <div
                                            className={`flex w-full transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                                        >
                                            <div className="w-5 flex justify-center ml-4 relative">
                                                <div className="w-[2px] bg-gray-300 absolute top-0 bottom-0"></div>
                                            </div>
                                            <div className="flex flex-col w-full gap-2">
                                                {item.subItems.map((subItem) => {
                                                    const isSubActive =
                                                        subItem.href === activeSubHref;
                                                    return (
                                                        <button
                                                            key={subItem.href}
                                                            onClick={() =>
                                                                !subItem.soon &&
                                                                navigate(subItem.href)
                                                            }
                                                            disabled={subItem.soon}
                                                            title={
                                                                subItem.soon
                                                                    ? 'Coming soon'
                                                                    : undefined
                                                            }
                                                            className={`flex h-8 items-center px-3 w-full rounded-lg transition-colors duration-200 ${
                                                                subItem.soon
                                                                    ? 'cursor-not-allowed'
                                                                    : 'hover:bg-gray-100'
                                                            } ${isSubActive ? 'bg-gray-200' : ''}`}
                                                        >
                                                            <span
                                                                className={`text-sm truncate min-w-0 ${
                                                                    subItem.soon
                                                                        ? 'font-medium text-gray-400'
                                                                        : isSubActive
                                                                          ? 'font-semibold text-gray-900'
                                                                          : 'font-medium text-gray-700'
                                                                }`}
                                                            >
                                                                {subItem.label}
                                                            </span>
                                                            {subItem.soon && (
                                                                <span className="ml-auto text-[10px] font-semibold text-gray-400 border border-gray-200 rounded px-1 py-px">
                                                                    SOON
                                                                </span>
                                                            )}
                                                            <NavCountBadge badge={subItem.badge} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </nav>
            </div>

            {/* Collapsed-mode flyout submenu (portalled to body) */}
            {flyout?.anchor && flyoutItem?.subItems && (
                <SidebarFlyout
                    label={flyoutItem.label}
                    icon={flyoutItem.icon}
                    subItems={flyoutItem.subItems}
                    anchor={flyout.anchor}
                    currentPath={location.pathname}
                    onSelect={(href) => {
                        navigate(href);
                        closeFlyout();
                    }}
                />
            )}

            {/* Bottom section: What's new, logout */}
            <div className="flex flex-col gap-1.5 px-2 pb-6">
                <WhatsNewNavItem collapsed={collapsed} />
                <hr className="mx-3 border-gray-200" />
                <Tooltip content="Log out" side="right" disabled={!collapsed}>
                    <button
                        onClick={handleLogout}
                        className={`flex h-10 items-center gap-2 px-3 py-2 w-full rounded-lg transition-colors duration-200 hover:bg-gray-100 text-gray-700 ${collapsed ? 'justify-center' : ''}`}
                    >
                        <img src={logoutIcon} alt="Logout" className="h-4 w-4 flex-shrink-0" />
                        {!collapsed && <span className="text-sm font-medium">Log out</span>}
                    </button>
                </Tooltip>
            </div>
        </aside>
    );
};
